import React from 'react';
import {
  AuditOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileProtectOutlined,
  LockOutlined,
  MailOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ShareAltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useCommunityLinks } from '../../../core/config/useCommunityLinks';
import LegalPage, { LegalNavigationItem, LegalSummaryItem } from './LegalPage';
import LegalSection from './LegalSection';

const UPDATED_AT = 'August 12, 2026';

const navigation: readonly LegalNavigationItem[] = [
  { id: 'overview', label: 'Overview', icon: <AuditOutlined /> },
  {
    id: 'information',
    label: 'Information we collect',
    icon: <DatabaseOutlined />,
  },
  { id: 'use', label: 'How we use it', icon: <FileProtectOutlined /> },
  { id: 'resume-ai', label: 'Resume & AI tools', icon: <RobotOutlined /> },
  { id: 'sharing', label: 'Sharing', icon: <ShareAltOutlined /> },
  { id: 'retention', label: 'Retention', icon: <ClockCircleOutlined /> },
  { id: 'rights', label: 'Your rights', icon: <SafetyCertificateOutlined /> },
  { id: 'contact', label: 'Contact', icon: <MailOutlined /> },
] as const;

const summaryItems: readonly LegalSummaryItem[] = [
  {
    title: 'Purpose comes first',
    description:
      'We use personal information to provide job-search, application, alert, and career-tool features.',
    icon: <FileProtectOutlined />,
  },
  {
    title: 'You stay in control',
    description:
      'You may ask to access, correct, or delete personal information, subject to applicable law.',
    icon: <UserOutlined />,
  },
  {
    title: 'Security by design',
    description:
      'We apply safeguards intended to protect information while it is processed and stored.',
    icon: <LockOutlined />,
  },
] as const;

const PrivacyPolicy: React.FC = () => {
  const { contactEmail } = useCommunityLinks();

  return (
    <LegalPage
      title="Privacy Policy"
      description="Your data, explained clearly."
      updatedAt={UPDATED_AT}
      icon={<SafetyCertificateOutlined />}
      navigation={navigation}
      summaryTitle="At a glance"
      summaryItems={summaryItems}
    >
      <LegalSection id="overview" title="Overview">
        <p>
          This Privacy Policy explains how Jobs &amp; Opportunities collects,
          uses, discloses, and protects personal information when you browse job
          listings, apply for a role, subscribe to alerts, register as a
          recruiter, or use our resume and job-fit tools.
        </p>
        <p>
          By using the service, you acknowledge the practices described here.
          Some features may provide an additional notice at the moment you
          submit information.
        </p>
      </LegalSection>

      <LegalSection id="information" title="Information we collect">
        <div className="legal-table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Examples</th>
                <th scope="col">Where it comes from</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Identity and contact</td>
                <td>
                  Name, email, phone number, country, professional profile
                </td>
                <td>You, a recruiter account, or resume extraction</td>
              </tr>
              <tr>
                <td>Application and career</td>
                <td>
                  Resume, skills, work history, salary expectations,
                  applications
                </td>
                <td>You and the materials you upload</td>
              </tr>
              <tr>
                <td>Account and preferences</td>
                <td>
                  Recruiter registration, job-alert filters, email or WhatsApp
                  choices
                </td>
                <td>You and your use of the service</td>
              </tr>
              <tr>
                <td>Technical and usage</td>
                <td>
                  Device, browser, IP address, pages viewed, interactions, and
                  logs
                </td>
                <td>Your device, cookies, analytics, and server logs</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Please do not include sensitive personal information in a resume
          unless it is relevant and lawful to provide for the application.
        </p>
      </LegalSection>

      <LegalSection id="use" title="How we use your information">
        <ul>
          <li>
            Display job listings and operate search and filtering features.
          </li>
          <li>
            Submit applications and make them available to the relevant hiring
            team.
          </li>
          <li>
            Prefill forms by extracting information from an uploaded resume.
          </li>
          <li>Deliver requested job alerts and application communications.</li>
          <li>Register recruiters and support job-posting workflows.</li>
          <li>Secure, maintain, troubleshoot, and improve the service.</li>
          <li>Measure usage and understand which features are helpful.</li>
          <li>
            Comply with legal obligations and enforce our Terms and Conditions.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="resume-ai" title="Resume and AI-assisted tools">
        <div className="legal-callout legal-callout--info">
          <RobotOutlined aria-hidden="true" />
          <div>
            <h3>AI provides assistance, not hiring decisions</h3>
            <p>
              Resume improvement and job-fit features may analyze resume text,
              infer skills, suggest edits, and compare experience with a job
              description. Results are informational and may be incomplete or
              inaccurate. They do not guarantee an interview or employment.
            </p>
          </div>
        </div>
        <p>
          When you upload a resume, the file and extracted information are sent
          to our systems and service providers as needed to produce the feature
          you requested. Hiring decisions remain with recruiters and employers;
          Jobs &amp; Opportunities does not make employment decisions through a
          job-fit score.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="When we share information">
        <p>We may disclose personal information to:</p>
        <ul>
          <li>
            <strong>Recruiters and employers</strong> when you submit an
            application or otherwise ask us to connect you with an opportunity.
          </li>
          <li>
            <strong>Service providers</strong> that support hosting, storage,
            communications, analytics, security, and resume-processing features.
          </li>
          <li>
            <strong>Authorities or other parties</strong> when reasonably
            necessary to comply with law, protect rights and safety, or address
            fraud and abuse.
          </li>
          <li>
            <strong>A successor organization</strong> in connection with a
            merger, financing, acquisition, or transfer of the service, subject
            to appropriate safeguards.
          </li>
        </ul>
        <p>We do not sell personal information.</p>
      </LegalSection>

      <LegalSection id="cookies" title="Cookies and analytics">
        <p>
          We use local storage for preferences such as theme selection and may
          use analytics technologies, including Google Analytics, to understand
          service usage. Depending on your location, we will request consent
          before using non-essential analytics technologies where required by
          law. Browser settings may also allow you to restrict cookies or clear
          stored information.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="How long we keep information">
        <p>
          We retain personal information only for as long as reasonably needed
          for the purposes described in this policy, including delivering the
          requested service, maintaining application and account records,
          resolving disputes, preventing abuse, and meeting legal obligations.
        </p>
        <p>
          Retention depends on the feature used, whether an account or
          subscription remains active, the hiring organization&apos;s
          requirements, and applicable law. Information may remain in protected
          backups for a limited period after deletion from active systems.
        </p>
      </LegalSection>

      <LegalSection id="rights" title="Your privacy rights">
        <p>
          Depending on where you live, you may have rights to request access,
          correction, deletion, restriction, portability, or objection to
          certain processing. You may also withdraw consent where processing is
          based on consent and unsubscribe from marketing or job alerts at any
          time.
        </p>
        <p>
          We may need to verify your identity before completing a request. Some
          information may be retained where required by law or necessary to
          establish, exercise, or defend legal claims.
        </p>
      </LegalSection>

      <LegalSection id="security" title="Security and international processing">
        <p>
          We use technical and organizational safeguards designed to protect
          personal information. No internet service can guarantee absolute
          security. Our providers may process information in countries other
          than your own; where required, we use appropriate safeguards for those
          transfers.
        </p>
      </LegalSection>

      <LegalSection id="children" title="Children's privacy">
        <p>
          The service is intended for people old enough to seek employment or
          recruit candidates under applicable law. It is not directed to
          children, and we do not knowingly collect personal information from
          children through the service.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to this policy">
        <p>
          We may update this policy as our service or legal obligations change.
          We will update the date above and provide additional notice when a
          change is material and applicable law requires it.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact us">
        <p>
          Questions or privacy requests may be sent
          {contactEmail ? (
            <>
              {' '}
              to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
            </>
          ) : (
            <> through the contact method published in the website footer.</>
          )}
        </p>
      </LegalSection>
    </LegalPage>
  );
};

export default PrivacyPolicy;
